import { IDependentValue } from '@publishing-engine/domain/models';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';

/**
 * Reads the "willing to review a revised version" answer out of an assessment.
 * The field has been recorded both as a dependent value and as a plain string,
 * so both shapes are read.
 */
export const resolveConsentToReInvite = (
  consentToReInvite?: string | IDependentValue<string>
): boolean | undefined => {
  if (typeof consentToReInvite === 'object') {
    return consentToReInvite.exists;
  }

  if (typeof consentToReInvite === 'string') {
    return consentToReInvite.toLowerCase() === 'yes'
      ? true
      : consentToReInvite.toLowerCase() === 'no'
      ? false
      : undefined;
  }

  return undefined;
};

/**
 * The consent recorded for the round on screen, falling back to the one from
 * round 1. A review's first assessment answers for round 1 and its revision
 * assessment for a revision round — the same split
 * `hasSubmittedReviewInCurrentRound` makes — so a revision round reads its own
 * assessment. But a reviewer awaiting reinvitation (or reinvited and yet to
 * submit) has no revision assessment of their own, and the only answer standing
 * is the one they gave in the first assessment; showing nothing there hides
 * exactly the consent editors read to decide who to reinvite.
 */
export const resolveRoundConsentToReInvite = (
  review: IPeerReviewListItem,
  isRevisionRound: boolean
): boolean | undefined => {
  if (isRevisionRound) {
    const revisionConsent = resolveConsentToReInvite(
      review.caseData.revisionAssessment?.consentToReInvite
    );
    if (revisionConsent !== undefined) {
      return revisionConsent;
    }
  }

  return resolveConsentToReInvite(
    review.caseData.firstAssessment?.consentToReInvite
  );
};
